import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import type { Product } from '../core/types/catalog';
import type { CartLine } from '../core/types/cart';
import { useCatalog } from './CatalogContext';
import { useSite } from './SiteContext';
import { codesIn, findCodeIn } from '../data/codes';
import type { AttributionCode } from '../core/types/site';
import type { OrderLineVia } from '../core/types/requests';
import { giftChoiceFor, nextTierIn, tierIn, unlockedIn, type GiftTier } from '../data/gifts';
import { freeShippingLine, policyCopy } from '../data/policies';
import { founder } from '../data/content';
import { toCents } from '../core/format/currency';
import { clampQty, isPhysical, isSoldOut, maxQty } from '../core/rules/stock';
import { canSubscribe } from '../core/rules/features';
import { allowedInterval, restoreLines, withBundleSubscribe, withoutBundle, withoutProduct, withRoutineBundle } from '../core/rules/cartBundles';
import { currentFeatures } from '../data/features';
import { useFeatures } from '../hooks/useFeatures';
import { track } from '../utils/analytics';
import { clampToStock, CART_KEY, hydrateLines, parseStoredCart, readStoredCart, viaOf, writeStoredCart, type CartAdjustment } from '../utils/cartStorage';
export type { CartLine, CartAdjustment };
export { storedCartLines } from '../utils/cartStorage';

/**
 * Geen eigen kortingen of coupons: de regelprijs is `product.price`, precies het bedrag dat
 * de server rekent (bij een goedgekeurde leveranciersactie de actieprijs). Ook een protocol als
 * bundel is de som van de losse producten (geen bundelkorting). Zo lopen mand, verzendgrens en
 * cadeaustaffel gelijk met de server. Wijkt het toch af, dan weigert de server de bestelling via
 * `expectedTotalCents` (zie useCheckout).
 */
function lineUnitPrice(line: CartLine) {
  return line.product.price;
}
export type { AttributionCode };

/**
 * De winkelstate is opgesplitst in vijf contexts, zodat een component alleen opnieuw
 * tekent als het stuk dat hij gebruikt verandert:
 *
 *  - useCart()           → inhoud en bedragen (winkelmand, afrekenen)
 *  - useCartCount()      → alleen het aantal (badge in de navigatie)
 *  - useCartActions()    → toevoegen, aantal, verwijderen… (stabiel: tekent nooit opnieuw)
 *  - useOverlays()       → welke lade open is
 *  - useOverlayActions() → lades openen/sluiten (stabiel)
 */
export type CartState = {
  lines: CartLine[];
  count: number;
  subtotal: number;
  /** Subtotaal zonder cadeaubonnen: telt voor gratis verzending en de cadeaustaffel. */
  giftSubtotal: number;
  shipping: number;
  total: number;
  hasSubscription: boolean;
  /** Het ritme voor alles wat bijgevuld wordt: de keuze van de klant, anders de standaard. */
  subscriptionIntervalWeeks: number;
  unlockedGift: GiftTier | null;
  /** Alle vrijgespeelde cadeaus (zonder gratis verzending), met de keuze van de klant. */
  gifts: {
    tier: GiftTier;
    choice: string | null;
  }[];
  nextGift: GiftTier | null;
  giftTiers: GiftTier[];
  appliedCode: AttributionCode | null;
  /** Wat we bij het bijwerken van de mand aanpasten (product weg, aantal omlaag); leeg = niets te melden. */
  adjustments: CartAdjustment[];
};
export type SubscriptionPick = {
  /** Een heel protocol, als bundel. */
  routineId?: string;
  /** Losse producten met hun aantal. */
  items: {
    product: Product;
    qty: number;
  }[];
  intervalWeeks: number;
};
export type CartActions = {
  /** `false` als er niets bij kwam (uitverkocht of maximum bereikt); de melding regelt `add` zelf. */
  add: (product: Product, opts?: {
    subscribe?: boolean;
    silent?: boolean;
    via?: OrderLineVia;
  }) => boolean;
  /** Een protocol als bundel. `subscribe` = bijvullen (nooit vooraf aan), met `intervalWeeks` als ritme. */
  addRoutine: (routineId: string, opts?: {
    via?: OrderLineVia;
    subscribe?: boolean;
    intervalWeeks?: number;
  }) => void;
  /** Uit de abonnementsamensteller: precies dit wordt bijgevuld, in dit ritme. */
  addSubscription: (pick: SubscriptionPick) => void;
  toggleSubscribe: (id: string) => void;
  /** Eén keuze voor eenmalig of bijvullen voor de hele bundel. */
  setBundleSubscribe: (routineId: string, subscribe: boolean) => void;
  setSubscriptionInterval: (weeks: number) => void;
  setQty: (id: string, qty: number) => void;
  remove: (id: string) => void;
  /** De hele bundel eruit, met "Ongedaan maken". */
  removeBundle: (routineId: string) => void;
  clearCart: () => void;
  applyCode: (code: string) => boolean;
  removeCode: () => void;
  /** Bijv. welk reisformaat. Per cadeauregel (`ruleId`). */
  chooseGift: (ruleId: string, choice: string) => void;
  /** De melding "Je winkelmand is bijgewerkt" wegklikken. */
  dismissAdjustments: () => void;
};
export type OverlayState = {
  cartOpen: boolean;
  /** Slug van de behandeling waarmee de afsprakenlade opent. */
  bookingTreatment: string | null;
  bookingOpen: boolean;
  quizOpen: boolean;
  /** De abonnementsamensteller. */
  builderOpen: boolean;
};
export type OverlayActions = {
  openCart: () => void;
  closeCart: () => void;
  openBooking: (treatmentSlug?: string) => void;
  closeBooking: () => void;
  openQuiz: () => void;
  closeQuiz: () => void;
  openSubscriptionBuilder: () => void;
  closeSubscriptionBuilder: () => void;
  /** Alle lades dicht (bij een paginawissel). */
  closeAll: () => void;
};
const CartContext = createContext<CartState | null>(null);
const CartCountContext = createContext<number | null>(null);
const CartActionsContext = createContext<CartActions | null>(null);
const OverlayContext = createContext<OverlayState | null>(null);
const OverlayActionsContext = createContext<OverlayActions | null>(null);

/** In centen rekenen voorkomt afrondingsverschillen (0,1 + 0,2). */
const cents = toCents;
const sum = (ls: CartLine[]) => ls.reduce((n, l) => n + l.qty * cents(lineUnitPrice(l)), 0) / 100;
const refillable = (p: Product) => canSubscribe(currentFeatures(), p);
export function StoreProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    routineById,
    routineTotals,
    productById
  } = useCatalog();
  const features = useFeatures();
  // Instellingen als state: verandert de verzendgrens of de staffel, dan rekent de mand opnieuw.
  const site = useSite();
  const {
    policy,
    giftTiers
  } = site;
  const allCodes = useMemo(() => codesIn(site), [site]);
  const findCode = (input: string) => findCodeIn(allCodes, input);
  // Acties zijn stabiel: zij lezen de actuele instellingen via een ref.
  const siteRef = useRef({
    allCodes,
    freeShippingFrom: policy.freeShippingFrom,
    intervals: policy.subscription.intervals
  });
  siteRef.current = {
    allCodes,
    freeShippingFrom: policy.freeShippingFrom,
    intervals: policy.subscription.intervals
  };
  const [stored] = useState(readStoredCart);
  const [initial] = useState(() => hydrateLines(stored, productById));
  const [lines, setLines] = useState<CartLine[]>(initial.lines);
  const [adjustments, setAdjustments] = useState<CartAdjustment[]>(initial.adjustments);
  const [intervalWeeks, setIntervalWeeks] = useState<number | null>(() => typeof stored?.intervalWeeks === 'number' ? stored.intervalWeeks : null);
  const [cartOpen, setCartOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingTreatment, setBookingTreatment] = useState<string | null>(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [appliedCode, setAppliedCode] = useState<AttributionCode | null>(() => stored?.code ? findCode(stored.code) : null);
  const [giftChoices, setGiftChoices] = useState<Record<string, string>>(() => stored?.giftChoices ?? {});
  useEffect(() => {
    writeStoredCart(lines, appliedCode?.code ?? null, giftChoices, intervalWeeks);
  }, [lines, appliedCode, giftChoices, intervalWeeks]);

  // Meerdere tabbladen: het `storage`-event komt alleen binnen in de ANDERE tabbladen,
  // dus er ontstaat geen lus. Schrijft dit tabblad daarna dezelfde JSON terug, dan
  // verandert de waarde niet en vuurt er ook geen nieuw event. `key === null` = opslag gewist.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== CART_KEY) return;
      const next = parseStoredCart(e.key === null ? null : e.newValue);
      // Het andere tabblad heeft zijn mand al bijgewerkt (en daar gemeld); hier alleen overnemen.
      setLines(hydrateLines(next, productById).lines);
      setAppliedCode(next?.code ? findCodeIn(siteRef.current.allCodes, next.code) : null);
      setGiftChoices(next?.giftChoices ?? {});
      setIntervalWeeks(typeof next?.intervalWeeks === 'number' ? next.intervalWeeks : null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [productById]);

  // `lines` via een ref lezen, zodat de acties kunnen controleren zonder bij elke wijziging opnieuw te worden gemaakt.
  const linesRef = useRef(lines);
  linesRef.current = lines;

  // Nieuwe catalogus (verversen, of de server na de lokale terugval): aantallen boven de voorraad omlaag, met melding.
  const firstCatalog = useRef(productById);
  useEffect(() => {
    if (firstCatalog.current === productById) return;
    firstCatalog.current = productById;
    const {
      lines: next,
      adjustments: changed
    } = clampToStock(linesRef.current, productById);
    if (!changed.length) return;
    setLines(next);
    setAdjustments(a => [...a.filter(x => !changed.some(c => c.name === x.name)), ...changed]);
  }, [productById]);
  // Eén lade tegelijk: de winkelmand openen sluit het huidadvies, de afsprakenlade en de samensteller.
  const showCart = useCallback(() => {
    setCartOpen(true);
    setQuizOpen(false);
    setBookingOpen(false);
    setBuilderOpen(false);
  }, []);
  const showCartRef = useRef(showCart);
  showCartRef.current = showCart;

  // Catalogus-opzoekers via een ref: komt de catalogus opnieuw binnen, dan blijven de acties toch stabiel.
  const catalogRef = useRef({
    routineById,
    routineTotals,
    productById
  });
  catalogRef.current = {
    routineById,
    routineTotals,
    productById
  };
  const chooseInterval = useCallback((weeks: number | undefined) => {
    const allowed = allowedInterval(siteRef.current.intervals, weeks);
    if (allowed !== null) setIntervalWeeks(allowed);
  }, []);
  const add = useCallback<CartActions['add']>((product, opts) => {
    // Eén plek voor de voorraadregel: elke "In winkelmand"-knop (kaart, quiz, artikel, upsell…) loopt hierlangs.
    if (isSoldOut(product)) {
      toast(`${product.name} is tijdelijk uitverkocht`, {
        description: `App ${founder.firstName} gerust voor een alternatief.`
      });
      return false;
    }
    const current = linesRef.current.find(l => l.product.id === product.id)?.qty ?? 0;
    if (current >= maxQty(product)) {
      toast(`Er kunnen er maximaal ${maxQty(product)} in je winkelmand`, {
        description: product.name
      });
      return false;
    }
    const subscribe = refillable(product) ? opts?.subscribe : false;
    setLines(prev => {
      const existing = prev.find(l => l.product.id === product.id);
      if (existing) {
        return prev.map(l => l.product.id === product.id ? {
          ...l,
          qty: Math.min(l.qty + 1, maxQty(product)),
          subscribe: subscribe ?? l.subscribe,
          ...viaOf(opts?.via ?? l.via)
        } : l);
      }
      return [...prev, {
        product,
        qty: 1,
        subscribe: subscribe ?? false,
        ...viaOf(opts?.via)
      }];
    });
    track('mand-toegevoegd', {
      product: product.id
    });
    if (!opts?.silent) {
      toast(`${product.name} toegevoegd`, {
        description: subscribe ? `Bewust Abonnement: ${policyCopy.subscriptionPerks}` : freeShippingLine(siteRef.current.freeShippingFrom),
        action: {
          label: 'Bekijk winkelmand',
          onClick: () => showCartRef.current()
        }
      });
    }
    return true;
  }, []);
  const addRoutine = useCallback<CartActions['addRoutine']>((routineId, opts) => {
    const routine = catalogRef.current.routineById(routineId);
    if (!routine) return;
    const {
      items: all
    } = catalogRef.current.routineTotals(routine);
    const items = all.filter(p => !isSoldOut(p));
    const skipped = all.filter(isSoldOut);
    if (skipped.length) {
      toast(`${skipped.map(p => p.name).join(', ')} ${skipped.length === 1 ? 'is' : 'zijn'} tijdelijk uitverkocht`, {
        description: items.length ? `De rest van het ${routine.name} staat in je winkelmand. ${founder.firstName} adviseert je graag een alternatief.` : `App ${founder.firstName} voor een alternatief.`
      });
    }
    if (!items.length) return;
    const subscribe = Boolean(opts?.subscribe);
    if (subscribe) chooseInterval(opts?.intervalWeeks);
    setLines(prev => withRoutineBundle(prev, routineId, items, {
      subscribe,
      via: opts?.via,
      canRefill: refillable
    }));
    track('mand-toegevoegd', {
      product: `protocol:${routineId}`
    });
    showCartRef.current();
  }, [chooseInterval]);
  const addSubscription = useCallback<CartActions['addSubscription']>(pick => {
    const picked = pick.items.filter(i => i.qty > 0 && !isSoldOut(i.product) && refillable(i.product));
    const routine = pick.routineId ? catalogRef.current.routineById(pick.routineId) : undefined;
    const routineItems = routine ? catalogRef.current.routineTotals(routine).items.filter(p => !isSoldOut(p)) : [];
    if (!picked.length && !routineItems.length) return;
    chooseInterval(pick.intervalWeeks);
    setLines(prev => {
      // De samensteller is de bron voor wat er bijgevuld wordt: wat er niet meer in zit, gaat terug naar eenmalig.
      const chosen = new Set([...picked.map(i => i.product.id), ...routineItems.map(p => p.id)]);
      let next = prev.map(l => l.subscribe && !chosen.has(l.product.id) ? {
        ...l,
        subscribe: false
      } : l);
      if (routine) next = withRoutineBundle(next, routine.id, routineItems, {
        subscribe: true,
        canRefill: refillable
      });
      for (const {
        product,
        qty
      } of picked) {
        const amount = clampQty(product, qty);
        const at = next.findIndex(l => l.product.id === product.id);
        if (at >= 0) next[at] = {
          ...next[at],
          qty: amount,
          subscribe: true
        };else next.push({
          product,
          qty: amount,
          subscribe: true
        });
      }
      return next;
    });
    track('mand-toegevoegd', {
      product: 'abonnement'
    });
    showCartRef.current();
  }, [chooseInterval]);
  const toggleSubscribe = useCallback((id: string) => {
    setLines(prev => prev.map(l => l.product.id === id && refillable(l.product) ? {
      ...l,
      subscribe: !l.subscribe
    } : l));
  }, []);
  const setBundleSubscribe = useCallback((routineId: string, subscribe: boolean) => {
    setLines(prev => withBundleSubscribe(prev, routineId, subscribe, refillable));
  }, []);

  // Weghalen kan per ongeluk (kleine knop, duim): één tik "Ongedaan maken" zet de regel terug op
  // dezelfde plek, met aantal, bijvullen en protocol. Uit een bundel: de rest wordt losse producten,
  // en "Ongedaan maken" maakt er weer een bundel van. Is hij intussen opnieuw toegevoegd, dan niets.
  const remove = useCallback((id: string) => {
    const at = linesRef.current.findIndex(l => l.product.id === id);
    const removed = linesRef.current[at];
    if (!removed) return;
    const {
      lines: next,
      split
    } = withoutProduct(linesRef.current, id);
    setLines(next);
    toast(`${removed.product.name} verwijderd`, {
      description: split ? 'Het protocol is nu losse producten.' : undefined,
      action: {
        label: 'Ongedaan maken',
        onClick: () => setLines(prev => {
          if (prev.some(l => l.product.id === id)) return prev;
          const rebundled = split ? prev.map(l => l.routineId === split.routineId && split.productIds.includes(l.product.id) ? {
            ...l,
            bundle: true
          } : l) : prev;
          return restoreLines(rebundled, [{
            line: removed,
            at
          }]);
        })
      }
    });
  }, []);
  const removeBundle = useCallback((routineId: string) => {
    const {
      lines: next,
      removed
    } = withoutBundle(linesRef.current, routineId);
    if (!removed.length) return;
    setLines(next);
    const name = catalogRef.current.routineById(routineId)?.name ?? 'Het protocol';
    toast(`${name} verwijderd`, {
      action: {
        label: 'Ongedaan maken',
        onClick: () => setLines(prev => restoreLines(prev, removed))
      }
    });
  }, []);
  const setQty = useCallback((id: string, qty: number) => {
    // Begrenzen met de actuele catalogus (voorraad kan veranderd zijn sinds het product in de mand kwam),
    // net als de weergave doet. Het product in de regel is alleen de terugval.
    const current = (l: CartLine) => catalogRef.current.productById(l.product.id) ?? l.product;
    const line = linesRef.current.find(l => l.product.id === id);
    if (!line) return;
    if (Number.isFinite(qty) && qty > maxQty(current(line))) {
      toast(`Er kunnen er maximaal ${maxQty(current(line))} in je winkelmand`, {
        description: line.product.name
      });
    }
    const next = clampQty(current(line), qty);
    // Naar nul = weghalen: uit een bundel valt die dan uiteen, net als bij "verwijderen".
    setLines(prev => next > 0 ? prev.map(l => l.product.id === id ? {
      ...l,
      qty: next
    } : l) : withoutProduct(prev, id).lines);
  }, []);
  const applyCode = useCallback((input: string) => {
    const found = findCodeIn(siteRef.current.allCodes, input);
    if (found) {
      setAppliedCode(found);
      return true;
    }
    return false;
  }, []);
  const cartActions = useMemo<CartActions>(() => ({
    add,
    addRoutine,
    addSubscription,
    toggleSubscribe,
    setBundleSubscribe,
    setSubscriptionInterval: weeks => chooseInterval(weeks),
    setQty,
    remove,
    removeBundle,
    applyCode,
    removeCode: () => setAppliedCode(null),
    chooseGift: (ruleId, choice) => setGiftChoices(c => ({
      ...c,
      [ruleId]: choice
    })),
    dismissAdjustments: () => setAdjustments([]),
    clearCart: () => {
      setLines([]);
      setAppliedCode(null);
      setGiftChoices({});
      setAdjustments([]);
      setIntervalWeeks(null);
    }
  }), [add, addRoutine, addSubscription, toggleSubscribe, setBundleSubscribe, chooseInterval, setQty, remove, removeBundle, applyCode]);
  const overlayActions = useMemo<OverlayActions>(() => {
    const only = (open: 'cart' | 'booking' | 'quiz' | 'builder' | null) => {
      setCartOpen(open === 'cart');
      setBookingOpen(open === 'booking');
      setQuizOpen(open === 'quiz');
      setBuilderOpen(open === 'builder');
    };
    return {
      openCart: showCart,
      closeCart: () => setCartOpen(false),
      openBooking: (treatment?: string) => {
        setBookingTreatment(treatment ?? null);
        only('booking');
      },
      closeBooking: () => setBookingOpen(false),
      openQuiz: () => only('quiz'),
      closeQuiz: () => setQuizOpen(false),
      openSubscriptionBuilder: () => only('builder'),
      closeSubscriptionBuilder: () => setBuilderOpen(false),
      closeAll: () => only(null)
    };
  }, [showCart]);
  const cart = useMemo<CartState>(() => {
    // Altijd de actuele productgegevens (prijs, voorraad) uit de catalogus — ook als die
    // na het openen van de site nog van de server binnenkomt.
    // Abonnement uitgezet: geen regel gaat nog als abonnement mee, ook niet naar afrekenen.
    // Kuur vooruit betalen uitgezet: een kuur in de mand valt weg.
    const live = lines.map(l => {
      const product = productById(l.product.id) ?? l.product;
      return {
        ...l,
        product,
        subscribe: l.subscribe && canSubscribe(features, product)
      };
    }).filter(l => features.prepaidKuur || l.product.kind !== 'kuur');
    const subtotal = sum(live);
    const giftSubtotal = sum(live.filter(l => isPhysical(l.product)));
    const hasSubscription = live.some(l => l.subscribe);
    // Alleen cadeaubonnen (digitaal) = niets te verzenden.
    const shipping = giftSubtotal === 0 || giftSubtotal >= policy.freeShippingFrom || hasSubscription ? 0 : policy.shippingCost;
    return {
      lines: live,
      count: live.reduce((n, l) => n + l.qty, 0),
      subtotal,
      giftSubtotal,
      shipping,
      total: (cents(subtotal) + cents(shipping)) / 100,
      hasSubscription,
      // Een ritme dat intussen uit de instellingen verdween, valt terug op de standaard.
      subscriptionIntervalWeeks: allowedInterval(policy.subscription.intervals, intervalWeeks) ?? policy.subscription.defaultIntervalWeeks,
      unlockedGift: tierIn(giftTiers, giftSubtotal),
      gifts: unlockedIn(giftTiers, policy.freeShippingFrom, giftSubtotal).map(tier => ({
        tier,
        choice: giftChoiceFor(tier, giftChoices)
      })),
      nextGift: nextTierIn(giftTiers, giftSubtotal),
      giftTiers,
      appliedCode,
      adjustments
    };
    // `policy` en `giftTiers` horen erbij: een live wijziging (verversen na 409) rekent de mand opnieuw.
  }, [lines, productById, appliedCode, giftChoices, features, adjustments, policy, giftTiers, intervalWeeks]);
  const overlays = useMemo<OverlayState>(() => ({
    cartOpen,
    bookingOpen,
    bookingTreatment,
    quizOpen,
    builderOpen
  }), [cartOpen, bookingOpen, bookingTreatment, quizOpen, builderOpen]);
  return <CartActionsContext.Provider value={cartActions} data-id="element-5554" data-mp="3qw34quhtciw8">
      <OverlayActionsContext.Provider value={overlayActions} data-id="element-5555" data-mp="3uw3kcignf04v">
        <CartCountContext.Provider value={cart.count} data-id="element-5556" data-mp="3uwn3g2i1bukh">
          <CartContext.Provider value={cart} data-id="element-5557" data-mp="mga5e81ytwpx">
            <OverlayContext.Provider value={overlays} data-id="element-5558" data-mp="mimur6a1z9d0">{children}</OverlayContext.Provider>
          </CartContext.Provider>
        </CartCountContext.Provider>
      </OverlayActionsContext.Provider>
    </CartActionsContext.Provider>;
}
function required<T>(value: T | null, hook: string): T {
  if (value === null) throw new Error(`${hook} moet binnen <StoreProvider> gebruikt worden`);
  return value;
}

/** Inhoud en bedragen van de winkelmand. Tekent opnieuw bij elke wijziging in de mand. */
export const useCart = () => required(useContext(CartContext), 'useCart');

/** Alleen het aantal producten — voor badges. */
export const useCartCount = () => required(useContext(CartCountContext), 'useCartCount');

/** Acties op de mand. Stabiel: gebruik dit in kaarten en knoppen. */
export const useCartActions = () => required(useContext(CartActionsContext), 'useCartActions');

/** Welke lade (winkelmand, afspraak, huidadvies, samensteller) open is. */
export const useOverlays = () => required(useContext(OverlayContext), 'useOverlays');

/** Lades openen en sluiten. Stabiel. */
export const useOverlayActions = () => required(useContext(OverlayActionsContext), 'useOverlayActions');